import React, { useContext, useEffect, useState } from 'react'
import Table from '../../../components/Table'
import styles from '../styles.module.scss'
import { useSelector } from 'react-redux'
import { DisciplineStructureContext } from '../../../utils/context'
import Loader from '../../../components/Loader'
import { COURSE_TYPE_CONFIG, TABLE_COLUMNS } from '../config'



export default function TableComponent() {

  const {
    tableData,
    selectedRow,
    setSelectedRow,
    setEditParams,
    buttonsBlockRef,
    tableAreaRef,
    form,
    editingRow,
    startEditingRow,
    confirmNewRow,
    cancelNewRow,
    updateEditingRow,
  } = useContext(DisciplineStructureContext)

  const [collapsedRows, setCollapsedRows] = useState(new Set())

  const courseType = form?.values?.course_type ?? 0
  const config = COURSE_TYPE_CONFIG[courseType]
  const columns = config.tableColumns.map(key => TABLE_COLUMNS[key])
  const data = tableData || []

  const { loading } = useSelector(state => state.discipline_structure || {})

  // Проверка выбраны ли обязательные фильтры
  const hasRequiredFilters = form?.values?.xp_key && form?.values?.xp_key !== 0
  const hasTeacherFilter = config.showTeacher ? (form?.values?.mid && form?.values?.mid !== 0) : true
  const hasCourseFilter = form?.values?.cid && form?.values?.cid !== 0
  const allFiltersSelected = hasRequiredFilters && hasTeacherFilter && hasCourseFilter

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick)
    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [selectedRow, editingRow])

  const selectRow = (row, idsList, depth) => () => {
    setSelectedRow(row)
    setEditParams({ idsList, depth })
  }

  const handleOutsideClick = (event) => {
    const path = event.path || (event.composedPath && event.composedPath());

    if (path.includes(buttonsBlockRef?.current) || path.includes(tableAreaRef?.current || selectedRow)) return;
    setSelectedRow(null)
    cancelNewRow()
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      confirmNewRow(editingRow.row)
    } else if (e.key === 'Escape') {
      cancelNewRow()
    }
  }

  const renderEditingRow = (level = 0) => {
    if (!editingRow) return null
    const paddingLeft = `${level * 100}px`

    return (
      <tr className={styles.editingRow} key="editing-row">
        {columns.map((col, colIndex) => (
          <td
            key={col.key}
            style={colIndex === 0 ? { paddingLeft } : undefined}
            className={colIndex === 0 ? styles.firstColumn : undefined}
          >
            {col.multiline ? (
              <textarea
                className={styles.editInput}
                value={editingRow.row[col.key] || ''}
                onChange={(e) => updateEditingRow(col.key, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) handleKeyDown(e)
                  else if (e.key === 'Escape') handleKeyDown(e)
                }}
                placeholder={col.header || 'Название'}
                autoFocus={colIndex === 0}
                maxLength={col.maxLength}
                rows={2}
              />
            ) : (
              <input
                type="text"
                className={styles.editInput}
                value={editingRow.row[col.key] || ''}
                onChange={(e) => updateEditingRow(col.key, e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={col.header || 'Название'}
                autoFocus={colIndex === 0}
                maxLength={col.maxLength}
              />
            )}
          </td>
        ))}
        <td className={styles.editActions}>
          <button
            className={styles.confirmBtn}
            onClick={() => confirmNewRow(editingRow.row)}
            title="Сохранить"
          >
            ✓
          </button>
          <button
            className={styles.cancelBtn}
            onClick={cancelNewRow}
            title="Отмена"
          >
            ✕
          </button>
        </td>
      </tr>
    )
  }

  const shouldShowEditingRowHere = (ids, index) => {
    if (!editingRow) return false
    const { parentIdsList, insertIndex } = editingRow

    if (!parentIdsList || parentIdsList.length === 0) {
      // Верхний уровень
      return (!ids || ids.length === 0) && index === insertIndex
    }

    // Проверяем, совпадает ли путь родителя
    if (!ids || ids.length !== parentIdsList.length) return false
    const isSameParent = parentIdsList.every((id, i) => ids[i] === id)
    return isSameParent && index === insertIndex
  }

  const toggleCollapse = (oid, e) => {
    e.stopPropagation()
    setCollapsedRows(prev => {
      const next = new Set(prev)
      next.has(oid) ? next.delete(oid) : next.add(oid)
      return next
    })
  }

  const renderRows = (ids) => (row, index) => {
    const { oid, _localId, children, level } = row
    const rowKey = _localId ?? oid
    const hasChildren = children?.length > 0
    const isCollapsed = collapsedRows.has(rowKey)
    const paddingLeft = `${level * 20}px`
    const idsList = ids ? [...ids, index] : [index]
    const depth = idsList.length - 1
    const isSelected = selectedRow && (selectedRow._localId ?? selectedRow.oid) === rowKey

    const isThisRowEditing = editingRow &&
      editingRow.isEditing &&
      editingRow.insertIndex === index &&
      editingRow.parentIdsList?.length === (ids?.length || 0) &&
      (!editingRow.parentIdsList?.length || editingRow.parentIdsList.every((id, i) => ids[i] === id))

    const showEditingBefore = !editingRow?.isEditing && shouldShowEditingRowHere(ids, index)

    const showEditingAsChild = editingRow &&
      !editingRow.isEditing &&
      editingRow.parentIdsList &&
      editingRow.parentIdsList.length === idsList.length &&
      editingRow.parentIdsList.every((id, i) => idsList[i] === id)

    const handleDoubleClick = () => {
      startEditingRow(row, idsList, depth)
    }

    return (
      <React.Fragment key={rowKey}>
        {showEditingBefore && renderEditingRow(level)}
        {isThisRowEditing ? (
          renderEditingRow(level)
        ) : (
          <tr
            onClick={selectRow(row, idsList, depth)}
            onDoubleClick={handleDoubleClick}
            className={isSelected ? styles.selectedRow : undefined}
          >
            {columns.map((col, colIndex) => (
              <td
                key={col.key}
                style={colIndex === 0 ? { paddingLeft } : undefined}
                className={colIndex === 0 ? styles.firstColumn : undefined}
              >
                {colIndex === 0 && (
                  <button
                    className={styles.toggleBtn}
                    style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
                    onClick={(e) => toggleCollapse(rowKey, e)}
                  >
                    {isCollapsed ? '+' : '−'}
                  </button>
                )}
                {row[col.key]}
              </td>
            ))}
          </tr>
        )}
        {showEditingAsChild && renderEditingRow(level + 1)}
        {!isCollapsed && children?.map(renderRows(idsList))}
      </React.Fragment>
    )
  }

  // Проверяем, нужно ли показать редактируемую строку в конце таблицы
  const showEditingAtEnd = editingRow &&
    (!editingRow.parentIdsList || editingRow.parentIdsList.length === 0) &&
    editingRow.insertIndex >= data.length


  // Определяем, что показывать
  const getEmptyMessage = () => {
    if (!allFiltersSelected) {
      const missingFilters = []
      if (!hasRequiredFilters) missingFilters.push('учебный год')
      if (!hasTeacherFilter) missingFilters.push('преподавателя')
      if (!hasCourseFilter) missingFilters.push('предмет')

      return `Выберите ${missingFilters.join(', ')}`
    }

    if (data.length === 0) {
      return 'Данные отсутствуют'
    }

    return null
  }

  const emptyMessage = getEmptyMessage()

  return (
    <div className={styles.tableWrapper}>
      {loading ? (
        <Loader />
      ) : emptyMessage ? (
        <h3 className={styles.emptyMessage}>{emptyMessage}</h3>
      ) : (
        <Table className={styles.table}>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key}>{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody ref={tableAreaRef}>
            {data.map(renderRows())}
            {showEditingAtEnd && renderEditingRow(0)}
          </tbody>
        </Table>
      )}
    </div>
  )
}
